
import React, { useEffect, useState } from "react";
import {
    Alert,
    Button,
    Card,
    Col,
    Divider,
    Input,
    Row,
    Spin,
    Tabs,
    Tag,
    Typography,
    message,
} from "antd";
import {
    ArrowLeftOutlined,
    DownloadOutlined,
    FileTextOutlined,
    SaveOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import api from "../api/api";

const { Title, Text } = Typography;
const { TextArea } = Input;

const CurriculumLessonEditor = () => {
    const { lessonPlanId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [topic, setTopic] = useState(
        location.state?.topic || null
    );

    const [lessonPlan, setLessonPlan] = useState(
        location.state?.lessonPlan || null
    );

    const [lessonNote, setLessonNote] = useState(
        location.state?.lessonNote || null
    );

    const [loading, setLoading] = useState(false);
    const [savingPlan, setSavingPlan] = useState(false);
    const [savingNote, setSavingNote] = useState(false);
    const [generatingNote, setGeneratingNote] = useState(false);

    useEffect(() => {
        if (lessonPlanId) {
            loadLessonPlan();
        }
    }, [lessonPlanId]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        const lessonNoteId = params.get("lesson_note");

        if (lessonNoteId) {
            loadLessonNote(lessonNoteId);
        }
    }, [location.search]);

    const loadLessonPlan = async () => {
        setLoading(true);

        try {
            const response = await api.get(
                `/api/lesson-plans/${lessonPlanId}/`
            );

            setLessonPlan(response.data);

            if (response.data.topic) {
                setTopic(response.data.topic);
            }
        } catch (error) {
            console.error(error);

            message.error(
                error.response?.data?.detail ||
                "Unable to load lesson plan."
            );
        } finally {
            setLoading(false);
        }
    };

    const loadLessonNote = async (id) => {
        try {
            const response = await api.get(
                `/api/lesson-notes/${id}/`
            );

            setLessonNote(response.data);

            if (response.data.topic && !topic) {
                setTopic(response.data.topic);
            }
        } catch (error) {
            console.error(error);

            message.error(
                error.response?.data?.detail ||
                "Unable to load lesson note."
            );
        }
    };

    const updatePlanField = (field, value) => {
        setLessonPlan((previous) => ({
            ...previous,
            [field]: value,
        }));
    };

    const updateNoteField = (field, value) => {
        setLessonNote((previous) => ({
            ...previous,
            [field]: value,
        }));
    };

    const saveLessonPlan = async () => {
        if (!lessonPlan?.id) {
            message.warning("There is no lesson plan to save.");
            return;
        }

        setSavingPlan(true);

        try {
            const response = await api.patch(
                `/api/lesson-plans/${lessonPlan.id}/`,
                lessonPlan
            );

            setLessonPlan(response.data);

            message.success("Lesson plan saved.");
        } catch (error) {
            console.error(error);

            message.error(
                error.response?.data?.detail ||
                "Unable to save lesson plan."
            );
        } finally {
            setSavingPlan(false);
        }
    };

    const saveLessonNote = async () => {
        if (!lessonNote?.id) {
            message.warning("There is no lesson note to save.");
            return;
        }

        setSavingNote(true);

        try {
            const response = await api.patch(
                `/api/lesson-notes/${lessonNote.id}/`,
                lessonNote
            );

            setLessonNote(response.data);

            message.success("Lesson note saved.");
        } catch (error) {
            console.error(error);

            message.error(
                error.response?.data?.detail ||
                "Unable to save lesson note."
            );
        } finally {
            setSavingNote(false);
        }
    };

    const generateLessonNote = async () => {
        if (!lessonPlan?.id) {
            message.warning(
                "Generate or save a lesson plan first."
            );
            return;
        }

        setGeneratingNote(true);

        try {
            const response = await api.post(
                "/api/lesson-notes/generate/",
                {
                    lesson_plan_id: lessonPlan.id,
                }
            );

            setLessonNote(response.data);

            message.success(
                "Lesson note generated successfully."
            );
        } catch (error) {
            console.error(error);

            message.error(
                error.response?.data?.detail ||
                "Unable to generate lesson note."
            );
        } finally {
            setGeneratingNote(false);
        }
    };

    const downloadPdf = (url, filename) => {
        if (!url) {
            message.warning(
                "PDF is not available yet."
            );
            return;
        }

        const finalUrl = url.startsWith("http")
            ? url
            : `${window.location.origin}${url}`;

        const link = document.createElement("a");

        link.href = finalUrl;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.download = filename;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (loading) {
        return (
            <div
                style={{
                    minHeight: "60vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div
            style={{
                padding: 24,
                maxWidth: 1400,
                margin: "0 auto",
            }}
        >
            <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(-1)}
                style={{ marginBottom: 20 }}
            >
                Back
            </Button>

            <Card
                style={{
                    borderRadius: 12,
                    marginBottom: 20,
                }}
            >
                <Title level={2}>
                    Lesson Editor
                </Title>

                {topic && (
                    <div>
                        <Tag color="blue">
                            {topic.subject?.name ||
                                topic.unit?.subject?.name ||
                                "Subject"}
                        </Tag>

                        <Tag color="green">
                            {topic.grade_class?.name ||
                                topic.unit?.grade_class?.name ||
                                "Grade/Class"}
                        </Tag>

                        <Text
                            strong
                            style={{ marginLeft: 8 }}
                        >
                            {topic.name}
                        </Text>
                    </div>
                )}
            </Card>

            {!lessonPlan && !lessonNote && (
                <Alert
                    type="info"
                    showIcon
                    message="No lesson content loaded"
                    description="Select a curriculum topic and generate a lesson plan or lesson note."
                />
            )}

            <Tabs
                defaultActiveKey="plan"
                items={[
                    {
                        key: "plan",
                        label: (
                            <span>
                                <FileTextOutlined />
                                Lesson Plan
                            </span>
                        ),
                        children: (
                            <LessonPlanEditor
                                lessonPlan={lessonPlan}
                                updateField={updatePlanField}
                                save={saveLessonPlan}
                                saving={savingPlan}
                                download={() =>
                                    downloadPdf(
                                        lessonPlan?.pdf_url,
                                        "lesson-plan.pdf"
                                    )
                                }
                            />
                        ),
                    },
                    {
                        key: "note",
                        label: "Lesson Note",
                        children: (
                            <LessonNoteEditor
                                lessonNote={lessonNote}
                                updateField={updateNoteField}
                                save={saveLessonNote}
                                saving={savingNote}
                                generating={generatingNote}
                                generate={generateLessonNote}
                                download={() =>
                                    downloadPdf(
                                        lessonNote?.pdf_url,
                                        "lesson-note.pdf"
                                    )
                                }
                            />
                        ),
                    },
                ]}
            />
        </div>
    );
};

const LessonPlanEditor = ({
    lessonPlan,
    updateField,
    save,
    saving,
    download,
}) => {
    if (!lessonPlan) {
        return (
            <Alert
                type="info"
                showIcon
                message="No lesson plan"
                description="Generate a lesson plan from a curriculum topic."
            />
        );
    }

    return (
        <Card
            style={{
                borderRadius: 12,
            }}
        >
            <Row gutter={[20, 20]}>
                <Col xs={24} md={12}>
                    <Field
                        label="Title"
                        value={lessonPlan.title}
                        onChange={(value) =>
                            updateField("title", value)
                        }
                    />
                </Col>

                <Col xs={24} md={12}>
                    <Field
                        label="Duration"
                        value={lessonPlan.duration}
                        onChange={(value) =>
                            updateField("duration", value)
                        }
                    />
                </Col>

                <Col xs={24}>
                    <Field
                        label="Learning Objectives"
                        value={lessonPlan.objectives}
                        onChange={(value) =>
                            updateField("objectives", value)
                        }
                    />
                </Col>

                <Col xs={24}>
                    <Field
                        label="Competencies"
                        value={lessonPlan.competencies}
                        onChange={(value) =>
                            updateField("competencies", value)
                        }
                    />
                </Col>

                <Col xs={24}>
                    <Field
                        label="Introduction"
                        value={lessonPlan.introduction}
                        onChange={(value) =>
                            updateField("introduction", value)
                        }
                    />
                </Col>

                <Col xs={24}>
                    <Field
                        label="Teacher Activities"
                        value={lessonPlan.teacher_activities}
                        onChange={(value) =>
                            updateField(
                                "teacher_activities",
                                value
                            )
                        }
                    />
                </Col>

                <Col xs={24}>
                    <Field
                        label="Learner Activities"
                        value={lessonPlan.learner_activities}
                        onChange={(value) =>
                            updateField(
                                "learner_activities",
                                value
                            )
                        }
                    />
                </Col>

                <Col xs={24}>
                    <Field
                        label="Assessment"
                        value={lessonPlan.assessment}
                        onChange={(value) =>
                            updateField("assessment", value)
                        }
                    />
                </Col>

                <Col xs={24}>
                    <Field
                        label="Resources"
                        value={lessonPlan.resources}
                        onChange={(value) =>
                            updateField("resources", value)
                        }
                    />
                </Col>

                <Col xs={24}>
                    <Field
                        label="Differentiation"
                        value={lessonPlan.differentiation}
                        onChange={(value) =>
                            updateField(
                                "differentiation",
                                value
                            )
                        }
                    />
                </Col>

                <Col xs={24}>
                    <Field
                        label="Homework"
                        value={lessonPlan.homework}
                        onChange={(value) =>
                            updateField("homework", value)
                        }
                    />
                </Col>
            </Row>

            <Divider />

            <div
                style={{
                    display: "flex",
                    gap: 10,
                    flexWrap: "wrap",
                }}
            >
                <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={saving}
                    onClick={save}
                >
                    Save Lesson Plan
                </Button>

                <Button
                    icon={<DownloadOutlined />}
                    onClick={download}
                >
                    Download Lesson Plan PDF
                </Button>
            </div>
        </Card>
    );
};

const LessonNoteEditor = ({
    lessonNote,
    updateField,
    save,
    saving,
    generating,
    generate,
    download,
}) => {
    if (!lessonNote) {
        return (
            <Card
                style={{
                    borderRadius: 12,
                }}
            >
                <Alert
                    type="info"
                    showIcon
                    message="Lesson note has not been generated"
                    description="Generate the lesson note from the lesson plan."
                />

                <Button
                    type="primary"
                    icon={<FileTextOutlined />}
                    loading={generating}
                    onClick={generate}
                    style={{ marginTop: 16 }}
                >
                    Generate Lesson Note
                </Button>
            </Card>
        );
    }

    return (
        <Card
            style={{
                borderRadius: 12,
            }}
        >
            <Row gutter={[20, 20]}>
                <Col xs={24}>
                    <Field
                        label="Title"
                        value={lessonNote.title}
                        onChange={(value) =>
                            updateField("title", value)
                        }
                    />
                </Col>

                <Col xs={24}>
                    <Field
                        label="Introduction"
                        value={lessonNote.introduction}
                        onChange={(value) =>
                            updateField("introduction", value)
                        }
                    />
                </Col>

                <Col xs={24}>
                    <Field
                        label="Lesson Content"
                        value={lessonNote.content}
                        onChange={(value) =>
                            updateField("content", value)
                        }
                        rows={12}
                    />
                </Col>

                <Col xs={24}>
                    <Field
                        label="Examples"
                        value={lessonNote.examples}
                        onChange={(value) =>
                            updateField("examples", value)
                        }
                    />
                </Col>

                <Col xs={24}>
                    <Field
                        label="Class Activities"
                        value={lessonNote.activities}
                        onChange={(value) =>
                            updateField("activities", value)
                        }
                    />
                </Col>

                <Col xs={24}>
                    <Field
                        label="Assessment Questions"
                        value={lessonNote.assessment}
                        onChange={(value) =>
                            updateField("assessment", value)
                        }
                    />
                </Col>

                <Col xs={24}>
                    <Field
                        label="Homework"
                        value={lessonNote.homework}
                        onChange={(value) =>
                            updateField("homework", value)
                        }
                    />
                </Col>

                <Col xs={24}>
                    <Field
                        label="Teaching Resources"
                        value={lessonNote.resources}
                        onChange={(value) =>
                            updateField("resources", value)
                        }
                    />
                </Col>
            </Row>

            <Divider />

            <div
                style={{
                    display: "flex",
                    gap: 10,
                    flexWrap: "wrap",
                }}
            >
                <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={saving}
                    onClick={save}
                >
                    Save Lesson Note
                </Button>

                <Button
                    icon={<DownloadOutlined />}
                    onClick={download}
                >
                    Download Lesson Note PDF
                </Button>
            </div>
        </Card>
    );
};

const Field = ({
    label,
    value,
    onChange,
    rows = 5,
}) => {
    return (
        <div>
            <Text
                strong
                style={{
                    display: "block",
                    marginBottom: 8,
                }}
            >
                {label}
            </Text>

            <TextArea
                value={value || ""}
                onChange={(event) =>
                    onChange(event.target.value)
                }
                rows={rows}
                style={{
                    whiteSpace: "pre-wrap",
                }}
            />
        </div>
    );
};

export default CurriculumLessonEditor;

